import { toast } from "sonner";
import {
  CheckCircle,
  XCircle,
  Calculator,
  CreditCard,
  Clock,
  AlertTriangle,
} from "lucide-react";

/**
 * Badge types for different toast notification categories
 */
export enum BadgeType {
  PAYMENT = "payment",
  REVERSAL = "reversal",
  AMOUNT = "amount",
  ACCOUNT = "account",
  SCHEDULED = "scheduled",
  ERROR = "error",
  SUCCESS = "success",
}

/**
 * Badge configuration with icons and colors
 */
export const badgeConfig = {
  [BadgeType.PAYMENT]: {
    icon: CheckCircle,
    emoji: "💰",
    label: "Pagamento",
    colorLight: "bg-green-500",
    colorDark: "dark:bg-green-600",
    textColor: "text-white",
  },
  [BadgeType.REVERSAL]: {
    icon: XCircle,
    emoji: "↩️",
    label: "Reversão",
    colorLight: "bg-amber-500",
    colorDark: "dark:bg-amber-600",
    textColor: "text-white",
  },
  [BadgeType.AMOUNT]: {
    icon: Calculator,
    emoji: "💵",
    label: "Valor",
    colorLight: "bg-orange-500",
    colorDark: "dark:bg-orange-600",
    textColor: "text-white",
  },
  [BadgeType.ACCOUNT]: {
    icon: CreditCard,
    emoji: "🏦",
    label: "Transferência",
    colorLight: "bg-purple-500",
    colorDark: "dark:bg-purple-600",
    textColor: "text-white",
  },
  [BadgeType.SCHEDULED]: {
    icon: Clock,
    emoji: "📅",
    label: "Agendamento",
    colorLight: "bg-blue-500",
    colorDark: "dark:bg-blue-600",
    textColor: "text-white",
  },
  [BadgeType.ERROR]: {
    icon: AlertTriangle,
    emoji: "⚠️",
    label: "Erro",
    colorLight: "bg-red-500",
    colorDark: "dark:bg-red-600",
    textColor: "text-white",
  },
  [BadgeType.SUCCESS]: {
    icon: CheckCircle,
    emoji: "✅",
    label: "Sucesso",
    colorLight: "bg-green-400",
    colorDark: "dark:bg-green-500",
    textColor: "text-white",
  },
};

/**
 * Options for enhanced toast notifications
 */
export interface EnhancedToastOptions {
  badge?: BadgeType;
  description?: string;
  metadata?: string;
  duration?: number;
  id?: string;
}

/**
 * Generate unique toast ID based on action type and timestamp
 */
export const createToastId = (type: string, context?: string): string => {
  const timestamp = Date.now();
  const base = context ? `${type}-${context}` : type;
  return `${base}-${timestamp}`;
};

/**
 * Toast queue for managing rapid consecutive toasts
 */
class ToastQueue {
  private queue: Map<string, { count: number; timer: NodeJS.Timeout }> = new Map();
  private debounceTime = 300; // ms

  consolidate(key: string, callback: (count: number) => void) {
    const existing = this.queue.get(key);

    if (existing) {
      clearTimeout(existing.timer);
      existing.count++;
    } else {
      this.queue.set(key, { count: 1, timer: setTimeout(() => {}, 0) });
    }

    const current = this.queue.get(key)!;
    current.timer = setTimeout(() => {
      callback(current.count);
      this.queue.delete(key);
    }, this.debounceTime);
  }
}

const toastQueue = new ToastQueue();

/**
 * Helper function to show payment-related toasts
 */
export const showPaymentToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.PAYMENT];
  toast.success(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("payment"),
    description: options?.description,
  });
};

/**
 * Helper function to show reversal/unpaid toasts
 */
export const showReversalToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.REVERSAL];
  toast.warning(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("reversal"),
    description: options?.description,
  });
};

/**
 * Helper function to show amount update toasts
 */
export const showAmountToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.AMOUNT];
  toast.success(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("amount"),
    description: options?.description,
  });
};

/**
 * Helper function to show account transfer toasts
 */
export const showAccountToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.ACCOUNT];
  toast.success(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("account"),
    description: options?.description,
  });
};

/**
 * Helper function to show scheduled operation toasts
 */
export const showScheduledToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.SCHEDULED];
  toast.success(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("scheduled"),
    description: options?.description,
  });
};

/**
 * Helper function to show error toasts
 */
export const showErrorToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.ERROR];
  toast.error(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("error"),
    description: options?.description,
  });
};

/**
 * Helper function to show success toasts
 */
export const showSuccessToast = (
  message: string,
  options?: Omit<EnhancedToastOptions, "badge">
) => {
  const badge = badgeConfig[BadgeType.SUCCESS];
  toast.success(`${badge.emoji} ${message}`, {
    ...options,
    id: options?.id || createToastId("success"),
    description: options?.description,
  });
};

/**
 * Re-export toast for convenience
 */
export { toast };
